import type { WorkbookCatalogEntry, WorkbookSource } from '@erp/shared-types';
import { workbookSourceLabel } from './workbookSourceLabels';
import type { WorkbookCatalogKind } from './workbookCatalog';
import type { GroupChoice } from './useGroupChoices';
import {
  schemaGroupMembers,
  schemaGroupMembersValid,
  type SchemaGroup,
} from './workbookSchemaGroup';

/** One row of the Add data list: something the right pane can describe and add. */
export type AddDataItem =
  | { key: string; type: 'entry'; entry: WorkbookCatalogEntry }
  | { key: string; type: 'group'; group: SchemaGroup }
  /** A Gently object (a query builder subject area), read with the fields chosen. */
  | { key: `object:${string}`; type: 'object'; base: string; label: string }
  | { key: 'paste'; type: 'paste' };

/** A titled block of the Add data list. */
export interface AddDataSection {
  /**
   * A catalog kind, or `other` for the ways to add data that aren't catalog
   * entries. The `entity` section is Gently entities: the catalog's Entities and
   * the Gently objects together, one concept to a person.
   */
  kind: WorkbookCatalogKind | 'other';
  items: AddDataItem[];
}

export function entryItem(entry: WorkbookCatalogEntry): AddDataItem {
  return { key: `entry:${entry.id}`, type: 'entry', entry };
}

export function groupItem(group: SchemaGroup): AddDataItem {
  return { key: `group:${group.schemaId}`, type: 'group', group };
}

export function objectItem(object: {
  key: string;
  label: string;
}): AddDataItem {
  return {
    key: `object:${object.key}` as const,
    type: 'object',
    base: object.key,
    label: object.label,
  };
}

const OTHER_ITEMS: AddDataItem[] = [{ key: 'paste', type: 'paste' }];

/** The Gently objects whose label contains `query`, ignoring case; all of them for no query. */
export function matchingObjects<T extends { label: string }>(
  objects: readonly T[],
  query: string,
): T[] {
  const needle = query.trim().toLowerCase();
  return objects.filter((object) =>
    object.label.toLowerCase().includes(needle),
  );
}

/**
 * The Add data list: the catalog's sections in order, with the schemas Data
 * Sources share leading the Data sources section and the Gently objects
 * following the Entities as Gently entities, then the ways to add data that
 * aren't in the catalog (a pasted table). The other ways are always offered,
 * so a search that matches nothing still leaves them.
 */
export function addDataSections(
  sections: readonly {
    kind: WorkbookCatalogKind;
    entries: readonly WorkbookCatalogEntry[];
  }[],
  shownGroups: readonly SchemaGroup[],
  /** The Gently objects to list, already matched to the search. */
  objects: readonly { key: string; label: string }[] = [],
): AddDataSection[] {
  const catalog =
    objects.length && !sections.some((s) => s.kind === 'entity')
      ? [...sections, { kind: 'entity' as const, entries: [] }]
      : sections;
  return [
    ...catalog.map((section) => ({
      kind: section.kind,
      items: [
        ...(section.kind === 'data-source' ? shownGroups.map(groupItem) : []),
        ...section.entries.map(entryItem),
        ...(section.kind === 'entity' ? objects.map(objectItem) : []),
      ],
    })),
    { kind: 'other', items: OTHER_ITEMS },
  ];
}

/** The selected item, or the first catalog item when the selection is gone (added, or filtered out). */
export function resolveAddDataSelection(
  sections: readonly AddDataSection[],
  selectedKey: string | null,
): AddDataItem | null {
  const items = sections.flatMap((section) => section.items);
  return (
    items.find((item) => item.key === selectedKey) ??
    items.find((item) => item.type === 'entry' || item.type === 'group') ??
    items.at(0) ??
    null
  );
}

/**
 * What a person calls a catalog entry, and the raw identifier to show beside
 * it when that differs: a Data Source reads as "Gains Midwest / Item Master"
 * with its `import.…` table as the detail.
 */
export function entryTitle(
  entry: Pick<WorkbookCatalogEntry, 'name' | 'label'>,
): {
  title: string;
  detail: string | null;
} {
  const title = entry.label || entry.name;
  return { title, detail: title !== entry.name ? entry.name : null };
}

/**
 * A schema member's name without the schema it shares with every other
 * member: "Gains Midwest" for the catalog's "Gains Midwest / Item Master". A
 * member the capped catalog lacks keeps its table name.
 */
export function memberTitle(
  member: { id: string; name: string },
  schemaName: string,
  catalog: readonly WorkbookCatalogEntry[],
): string {
  return workbookSourceLabel(
    member,
    catalog.find((entry) => entry.id === member.id)?.label,
    schemaName,
  );
}

/** FNV-1a over `text`, 32 bits, from `seed`. */
function fnv1a(text: string, seed: number): number {
  let hash = seed >>> 0;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash;
}

/**
 * A UUID (version 4 in form) fixed by `key`: the same key always gives the
 * same id. It is a hash, so it names a preview read, never a stored Source.
 */
export function stableUuid(key: string): string {
  const hex = [0x811c9dc5, 0x01000193, 0x9e3779b9, 0x85ebca6b]
    .map((seed) => fnv1a(key, seed).toString(16).padStart(8, '0'))
    .join('');
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    `4${hex.slice(13, 16)}`,
    `${((parseInt(hex[16] ?? '0', 16) & 0x3) | 0x8).toString(16)}${hex.slice(17, 20)}`,
    hex.slice(20, 32),
  ].join('-');
}

/**
 * The Source an item previews, with an id fixed by the item so the preview
 * query is read once and shared, not refetched per render. A shared schema
 * previews through `groupPreviewSource`; the other ways to add data have none.
 */
export function previewSource(item: AddDataItem): WorkbookSource | null {
  const id = stableUuid(`preview:${item.key}`);
  if (item.type !== 'entry') return null;
  const { entry } = item;
  const base = { id, folder: '', name: entry.label || entry.name };
  switch (entry.kind) {
    case 'data-source':
      return { ...base, kind: 'data-source', linkId: entry.id };
    case 'mapping':
      return { ...base, kind: 'mapping', mappingId: entry.id };
    case 'stateful-table':
      return { ...base, kind: 'stateful-table', tableId: entry.id };
    case 'data-asset':
      return { ...base, kind: 'data-asset', dataAssetId: entry.id };
    case 'forecast-snapshots':
      return { ...base, kind: 'forecast-snapshots' };
    case 'entity':
      return { ...base, kind: 'entity', entity: entry.id };
  }
}

/**
 * What a shared schema's preview reads: the combination the user has chosen
 * (members left out, a fixed list, the label column), so the preview shows the
 * table Add combined table would add. None while that choice can't be added,
 * including when every member is left out.
 */
export function groupPreviewSource(
  group: SchemaGroup,
  choice: Pick<GroupChoice, 'auto' | 'excluded' | 'labelColumn'>,
): WorkbookSource | null {
  // Every member left out reads nothing, even with new members joining later.
  if (group.members.every((member) => choice.excluded.has(member.id)))
    return null;
  const members = schemaGroupMembers(
    group.members,
    [...choice.excluded],
    choice.auto,
  );
  if (!schemaGroupMembersValid(members)) return null;
  const label = choice.labelColumn.trim();
  return {
    id: stableUuid(`preview:group:${group.schemaId}`),
    kind: 'schema-group',
    folder: '',
    name: group.schemaName,
    schemaId: group.schemaId,
    members,
    ...(label ? { labelColumn: label } : {}),
  };
}
